<template>
  <transition name="frisbuy-slide" @after-enter="startView">
    <div
      class="frisbuy-preview-story frisbuy-preview-popup"
      ref="scrollWrapper"
      v-show="album"
      @touchmove.self.prevent
      @mousedown.stop
    >
      <div class="frisbuy-preview-wrapper" @mousedown.stop>
        <div class="frisbuy-preview-area">
          <story-slider
            :albums="albums"
            :album="album"
            ref="slider"
            :opened-goods="isGoodsPopupOpen"
            @change-current="changeCurrent"
            @swipeAlbum="swipePreview"
            @navigationTouchPause="$refs.slider.touchPause()"
            @navigationSwitchToNext="(step) => switchToNext(step)"
            @playSlide="() => (isPause = false)"
            @pauseSlide="() => (isPause = true)"
            :hasAudio="hasAudio"
            :playWithSound="playWithSound"
            @getSlideProgress="(value) => (slideProgress = value)"
          ></story-slider>

          <PreviewCloseButton
            class="frisbuy-preview-close-button--desktop"
            @click="$emit('close-preview')"
          />

          <SlideWrapperElements
            :postCount="postCount"
            :albumId="album.id"
            :currentPostIndex="album.runningPostIndex"
            :percentOfProgress="slideProgress"
            :isHideIg="settings.censorship.hide_ig"
            :isShowAccountLinks="settings.is_show_account_links == '1'"
            :igAccount="account"
            @selectStep="(index) => $emit('set-story', index)"
            @clickInstagram="clickInstagram"
            :igSubscribeButtonText="settings.widget.subscribeButton"
            :isPause="isPause"
            @clickPause="isPause ? playSlide() : pauseSlide()"
            :isHasSound="Boolean(storyMedia && storyMedia.videoUrl && hasAudio)"
            :isSoundOn="playWithSound"
            @clickMute="playWithSound = !playWithSound"
            @clickClose="$emit('close-preview')"
            :adsMarker="album.posts[album.runningPostIndex]?.adsMarker"
          />

          <GoodsButton
            v-if="
              isShowGoodsButton &&
              storyMedia.linkList &&
              storyMedia.linkList.length > 0
            "
            :isGoodsPopupOpen="isGoodsPopupOpen"
            :text="goodsButton.text"
            :isLink="goodsButton.iconName === 'link'"
            :linkUrl="storyMedia.linkList[0].link"
            @clickButton="toggleGoodsList()"
            @clickLink="clickLink(storyMedia.linkList[0])"
          />

          <PopupTransitionWrapper
            :isPopupOpen="isGoodsPopupOpen"
            @swipe="(direction) => swipeDownPopup(direction)"
          >
            <GoodsPopup
              v-if="isGoodsPopupOpen"
              :goods="storyMedia.linkList"
              :currencySymbol="settings.widget.currency"
              @clickLink="(good) => clickLink(good)"
              @closePopup="toggleGoodsList()"
            />
          </PopupTransitionWrapper>
        </div>

        <BrandingLogo
          v-if="!settings.widget.hideBrand"
          @click.self.stop
          :customerCode="settings.customerCode"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import { toRaw } from "vue";
import analytics from "../common/mixins/analytics";
import StorySlider from "./components/storySlider.vue";
import {
  GoodsPopup,
  GoodsButton,
  PreviewCloseButton,
  SlideWrapperElements,
  BrandingLogo,
} from "@/components/stories";
import PopupTransitionWrapper from "@/components/stories/GoodsPopup/PopupTransitionWrapper.vue";

export default {
  data() {
    return {
      inView: false,
      playWithSound: false,
      categoryPostfix: "SR",
      hasAudio: false,
      isGoodsPopupOpen: false,
      isSliderOpen: false,
      currentCountRetryFetchPosts: 0,
      maxCountRetryFetchPosts: 3,
      isShowGoodsButton: true,
      slideProgress: 0,
      isPause: false,
    };
  },

  emits: ["set-story", "set-album", "close-preview"],

  props: ["album", "albums", "settings"],

  mixins: [analytics],

  components: {
    StorySlider,
    GoodsPopup,
    GoodsButton,
    PreviewCloseButton,
    SlideWrapperElements,
    PopupTransitionWrapper,
    BrandingLogo,
  },

  provide() {
    return {
      preview: this,
      resumeStorie: this.resumeStorie,
      settings: this.settings,
    };
  },

  computed: {
    storyMedia() {
      const { album } = this;

      if (!album) return {};
      if (!album.posts[album.runningPostIndex]) return {};
      if (!album.posts[album.runningPostIndex || 0].media[0]) return {};

      return album.posts[album.runningPostIndex || 0].media[0];
    },

    albumIndex() {
      return this.albums.indexOf(toRaw(this.album));
    },

    account() {
      const { album } = this;

      if (!album) return;

      return album.posts[0].providerPost.authorUser;
    },

    isTablet: {
      cache: false,
      get() {
        return (
          Math.min(window.innerWidth, screen.width) < 568 ||
          Math.min(window.innerHeight, screen.height) < 426
        );
      },
    },

    hasLinks() {
      const { linkList } = this.storyMedia;

      return linkList.some(
        (link) =>
          !Object.hasOwn(link, "sku") ||
          (Object.hasOwn(link, "sku") && !Object.hasOwn(link.sku, "price"))
      );
    },

    goodsButton() {
      const { linkList } = this.storyMedia;

      let buttonText = "Показать товар";
      let buttonIconName = "goods";

      if (linkList && linkList.length !== 0) {
        const moreThanTwoLinks = linkList.length > 1;

        if (moreThanTwoLinks) {
          buttonText = "Показать товары";
        }

        if (this.hasLinks) {
          buttonText = "Перейти по ссылке";
          buttonIconName = "link";

          if (this.settings.dynamic_link_btn && linkList[0].title !== "") {
            buttonText = linkList[0].title;
          }

          if (moreThanTwoLinks) {
            buttonText = "Открыть детали";
            buttonIconName = "details";
          }
        }

        if (this.isGoodsPopupOpen) {
          buttonText = "Скрыть товар";
          buttonIconName = "goods";

          if (moreThanTwoLinks) {
            buttonText = "Скрыть товары";
          }

          if (
            !Object.hasOwn(linkList[0], "sku") ||
            !Object.hasOwn(linkList[0].sku, "price")
          ) {
            buttonText = "Закрыть детали";
            buttonIconName = "details";
          }
        }
      }

      return {
        iconName: buttonIconName,
        text: buttonText,
      };
    },

    postCount() {
      const { album, settings } = this;

      const { per_page } = settings;

      const maxPosts = window.parseInt(per_page) || 20;

      if (album && album.postCount > maxPosts) {
        return maxPosts;
      }

      return (album && album.postCount) || 0;
    },

    defaultSoundStatus() {
      return this.settings.defaultSoundStatus;
    },
  },

  methods: {
    playSlide() {
      this.isPause = false;
      this.$refs.slider.play();
    },

    pauseSlide() {
      this.isPause = true;
      this.$refs.slider.pause();
    },

    stopSlide() {
      this.$refs.slider.stop();
    },

    stopPreview() {
      const { slider } = this.$refs;

      slider.stop();
      this.inView = false; // not used

      this.unlockWindowScroll();
    },

    lockWindowScroll() {
      const customScrollElement = this.settings.scrollElement;
      if (customScrollElement) {
        document.querySelector(customScrollElement).style.overflow = "hidden";
      } else {
        document.body.style.overflow = "hidden";
      }
    },

    unlockWindowScroll() {
      const customScrollElement = this.settings.scrollElement;
      if (customScrollElement) {
        document.querySelector(customScrollElement).style.overflow = "";
      } else {
        document.body.style.overflow = "";
      }
    },

    clickInstagram() {
      this.sendAnalyticEvent("ClickInst", location.href, true, true);
    },

    clickLink(good) {
      if (!this.isGoodsPopupOpen) {
        document.addEventListener(
          "visibilitychange",
          this.handleVisibilitychange
        );
      }

      // [start] send analytic
      const story = this.album.posts[this.album.runningPostIndex];
      this.sendAnalyticEvent("ClickLink", {
        provPostId:
          story.instagramPost?.frisbuyId ?? story.providerPost.frisbuyId,
        fbProduct: story.frisbuyPost.id,
        igCode: story.providerPost
          ? story.providerPost.shortcode
          : story.instagramPost.shortcode,
        link: good.id,
        linklabel: good.title,
        offer: good.sku.offerId,
        linkUrl: good.link,
      });
      // [end] send analytic
    },

    swipePreview(direction) {
      if (this.isGoodsPopupOpen) {
        return;
      }

      const { albums, albumIndex, storyMedia, isGoodsPopupOpen } = this;
      const { linkList } = storyMedia;

      if (direction === "top" && linkList.length && !isGoodsPopupOpen) {
        if (this.goodsButton.iconName !== "link") {
          this.isGoodsPopupOpen = true;

          this.pauseSlide();
        }
      } else if (["left", "right"].includes(direction)) {
        const numberDirection = direction === "left" ? 1 : -1;
        const nextAlbum = albums[albumIndex + numberDirection];

        if (!nextAlbum) {
          this.$emit("close-preview");
          return;
        }

        this.$refs.slider.currentSlide.stop();
        this.changeCurrent(albumIndex + numberDirection);
      }
    },

    toggleGoodsList() {
      const { isGoodsPopupOpen } = this;

      if (this.goodsButton.iconName !== "link") {
        this.isGoodsPopupOpen = !isGoodsPopupOpen;
      }

      if (isGoodsPopupOpen) {
        this.playSlide();
      } else {
        this.pauseSlide();
      }
    },

    showGoodsButtonAnimation() {
      this.isShowGoodsButton = false;
      this.$nextTick(() => {
        this.isShowGoodsButton = true;
      });
    },

    switchToNext(step) {
      this.isPause = false;
      this.isGoodsPopupOpen = false;

      this.showGoodsButtonAnimation();

      const { albums, albumIndex, album, postCount } = this;

      if (album && postCount > album.runningPostIndex + (step || 1)) {
        if (album.runningPostIndex + (step || 1) >= 0) {
          this.$emit("set-story", album.runningPostIndex + (step || 1));
          return;
        }
      }

      const prevAlbum = albums[albumIndex - 1];

      const nextAlbum = albums[albumIndex + 1];

      albums[albumIndex].isAllStoriesWatched = true;

      if (nextAlbum && (!step || step === 1)) {
        this.stopSlide();
        this.changeCurrent(albumIndex + 1);
      } else if (prevAlbum && step === -1) {
        this.stopSlide();
        this.changeCurrent(albumIndex - 1);
      } else {
        if (step === 1) {
          this.$emit("close-preview");
        }
      }
    },

    changeCurrent(index) {
      if (isNaN(index) || this.albums[index] === this.album) return;

      this.$emit("set-album", this.albums[index]);

      this.$nextTick(() => {
        this.startView();
      });
    },

    async startView() {
      try {
        this.inView = true; // not used

        this.playSlide();

        this.$nextTick(() => {
          this.lockWindowScroll();
        });
      } catch (e) {
        this.switchToNext(1);
      }
    },

    checkAudio(video) {
      if (
        video.mozHasAudio ||
        Boolean(video.webkitAudioDecodedByteCount) ||
        Boolean(video.audioTracks && video.audioTracks.length)
      ) {
        this.hasAudio = true;
      } else {
        this.hasAudio = false;
      }
    },

    resumeStorie($event) {
      const { toggleGoodsList } = this;

      if (
        this.isGoodsPopupOpen &&
        !$event.path.includes(this.$refs.goodsPopup)
      ) {
        toggleGoodsList();
      }
    },

    handleVisibilitychange() {
      const { slider } = this.$refs;

      if (document.hidden && slider) {
        this.pauseSlide();
      }

      if (!document.hidden) {
        this.playSlide();
        document.removeEventListener(
          "visibilitychange",
          this.handleVisibilitychange
        );
      }
    },

    swipeDownPopup(direction) {
      if (
        direction === "bottom" &&
        this.storyMedia.linkList.length &&
        this.isGoodsPopupOpen
      ) {
        if (this.goodsButton.iconName !== "link") {
          this.isGoodsPopupOpen = false;

          this.playSlide();
        }
      }
    },
  },

  mounted() {
    const storySound = sessionStorage.getItem("frb_story_sound");
    if (storySound) {
      this.playWithSound = storySound === "true";
    } else {
      if (this.settings.defaultSoundStatus && this.settings.defaultSoundStatus === "1") {
        this.playWithSound = true;
      }
    }

    this.$nextTick(() => this.startView());

    document.body.append(this.$el);
  },

  watch: {
    album: {
      handler(newAlbum, oldAlbum) {
        const { isGoodsPopupOpen } = this.settings.widget;

        this.isGoodsPopupOpen = isGoodsPopupOpen || false;

        if (newAlbum && !oldAlbum) {
          // disableBodyScroll(this.$el, {
          //   allowTouchMove: (el) => {
          //     while (el && el !== document.body) {
          //       if (el.getAttribute("body-scroll-lock-ignore") !== null) {
          //         return true;
          //       }
          //       el = el.parentElement;
          //     }
          //   },
          // });
        }
      },
      immediate: true,
    },

    defaultSoundStatus() {
      if (this.settings.defaultSoundStatus) {
        this.playWithSound = this.settings.defaultSoundStatus === "1";
      }
    },

    playWithSound(withSound) {
      sessionStorage.setItem("frb_story_sound", Boolean(withSound).toString());
    },
  },
};
</script>

<style lang="scss">
@import "../common/style/mixins.scss";

.frisbuy-preview-story {
  --frisbuy-story-slide-height: 96dvh;
  --frisbuy-story-slide-width: calc(
    var(--frisbuy-story-slide-height) * (9 / 16)
  );

  @media screen and (max-width: 767px) {
    --frisbuy-story-slide-height: 100dvh !important;
  }
}

.frisbuy-preview-close-button {
  &.frisbuy-preview-close-button--desktop {
    position: absolute;
    top: 30px;
    right: 37px;

    width: 24px;
    height: 24px;

    @media (max-width: 767px) {
      display: none;
    }
  }
}

.frisbuy-preview-wrapper {
  .frisbuy-popup-transition-wrapper {
    @media screen and (min-width: 768px) {
      // HACK: 2px to cover the slide pixels on the bottom
      bottom: calc((100dvh - var(--frisbuy-story-slide-height) - 2px) / 2);
    }
  }
}

.frisbuy-preview-story {
  display: flex;
  flex-direction: column;

  width: 100dvw;
  height: 100dvh;

  z-index: 1;

  * {
    user-select: none;
    box-sizing: border-box;
  }

  &:not(.frisbuy-preview-popup) {
    transform: translateX(0);
    transition: 1s transform 1s;
    box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.24),
      0px 4px 16px rgba(0, 0, 0, 0.08);
    background-color: #fff;
    border-radius: 0 4px 4px 0;
    flex-grow: 1;

    &:before {
      content: "";
      width: 80px;
      height: 100%;
      box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.24),
        0px 4px 16px rgba(0, 0, 0, 0.08);
      position: absolute;
      transform: translateX(-80px);
    }

    &.frisbuy-slide-leave-active {
      transition: transform 1s;
      transform: translateX(-100%);
    }

    &.frisbuy-slide-enter {
      transform: translateX(-100%);
    }

    .frisbuy-preview-wrapper {
      flex-grow: 1;
    }

    .frisbuy-sr-branding {
      display: none;
    }
  }

  &.frisbuy-preview-popup {
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    z-index: 9999999999;
    position: fixed;
    overflow: hidden;
    transition: opacity 0.25s ease-in-out;
    will-change: opacity;
    transform: translateZ(0);
    display: block;
    opacity: 1;
    background: rgba(0, 0, 0, 0.9);

    .frisbuy-preview-wrapper {
      display: flex;
      position: initial;
    }

    .frisbuy-preview-area {
      order: 1;
      border-radius: 4px;
      -webkit-overflow-scrolling: auto;
      display: flex;
      justify-content: center;
      margin: 0 auto;

      width: var(--frisbuy-story-slide-width);
      margin-top: calc((96dvh - var(--frisbuy-story-slide-height)) / 2);
    }

    .frisbuy-account-controls {
      width: 50vh;
      order: 0;
      background-color: transparent;
      padding: 2px 0 2px;
    }

    .frisbuy-account-name {
      color: #fff;
    }

    .frisbuy-account-subscribe {
      color: #fff;
      border-color: #fff;
    }

    .frisbuy-preview-markers {
      &.frisbuy-row-view {
        margin-top: -7px;
      }
    }
  }

  .frisbuy-sr-branding {
    position: absolute;
    top: 95%;
    // left: calc(50% + 12vh);
    left: calc(50% + 26.1vh - 125px);
    z-index: 10;
  }

  .frisbuy-preview-wrapper {
    display: flex;
    flex-direction: column;
    position: relative;

    .frisbuy-sr-branding {
      right: 0;
    }
  }

  .frisbuy-preview-area {
    video {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .frisbuy-preview-markers {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 11;
    pointer-events: none;
    padding-bottom: 60px;

    &.frisbuy-row-view {
      display: flex;
      align-items: flex-start;
      flex-direction: column-reverse;

      .frisbuy-preview-marker {
        position: static;
        width: auto;
        height: auto;
        border-radius: 0;
        border: none;
      }

      .frisbuy-preview-good {
        position: static;
        opacity: 1;
        pointer-events: all;
        margin: 5px 5px 0 15px;
        width: 200px;
      }
    }
  }

  .frisbuy-preview-marker {
    position: absolute;
    width: 1px;
    height: 1px;

    &.frisbuy-direction-left {
      .frisbuy-preview-good {
        right: 100%;
        margin-right: 15px;

        &:before {
          content: "";
          position: absolute;
          border-width: 10px 0 10px 10px;
          border-style: solid;
          border-color: transparent #222222 transparent;
          left: 100%;
          top: 15px;
        }

        &:after {
          height: 100%;
          left: 100%;
          width: 20px;
        }
      }
    }

    &.frisbuy-direction-right {
      .frisbuy-preview-good {
        left: 100%;
        margin-left: 15px;

        &:before {
          right: 100%;
          border-width: 10px 10px 10px 0;
          border-color: transparent #222222 transparent;
          top: 15px;
        }

        &:after {
          height: 100%;
          right: 100%;
          width: 20px;
        }
      }
    }

    &.frisbuy-direction-bottom {
      .frisbuy-preview-good {
        top: 100%;
        left: -88px;
        margin-top: 15px;

        &:before {
          border-width: 0 10px 10px 10px;
          border-color: #222222 transparent;
          bottom: 100%;
          left: 77px;
        }

        &:after {
          height: 20px;
          bottom: 100%;
          width: 100%;
        }
      }
    }

    &.frisbuy-direction-top {
      .frisbuy-preview-good {
        top: auto;
        bottom: 100%;
        left: -88px;
        margin-bottom: 15px;

        &:before {
          border-width: 10px 10px 0 10px;
          border-color: #222222 transparent;
          top: 100%;
          left: 77px;
        }

        &:after {
          height: 20px;
          bottom: 100%;
          width: 100%;
        }
      }
    }

    .frisbuy-preview-good {
      position: absolute;
      opacity: 1;
      pointer-events: all;
      width: 176px;
      top: -10px;
      &:before {
        content: "";
        position: absolute;
        border-style: solid;
      }

      &:after {
        content: "";
        position: absolute;
      }
    }
  }

  .frisbuy-preview-good {
    padding: 8px;
    text-align: left;
    display: flex;
    align-items: center;
    background-color: #222222;
    color: #fff;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.01em;
    text-decoration: none;
    transition: opacity 0.3s;
    box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.24),
      0px 4px 16px rgba(0, 0, 0, 0.08);
    border-radius: 4px;

    svg {
      flex-shrink: 0;
    }
  }

  .frisbuy-preview-good-info {
    flex-grow: 1;
  }

  .frisbuy-preview-good-prices {
    display: flex;
    margin-top: 10px;
    align-items: center;
    justify-content: space-between;
  }

  .frisbuy-preview-good-old-price {
    text-decoration: line-through;
  }

  .frisbuy-short-toggler {
    background: #ffffff;
    font-size: 9px;
    line-height: 16px;
    display: flex;
    align-items: center;
    letter-spacing: 0.01em;
    border: 1px solid #222222;
    color: #222222;
    padding: 3px 8px;
    cursor: pointer;
    margin-left: 5px;
    box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.24),
      0px 4px 16px rgba(0, 0, 0, 0.08);
    pointer-events: all;

    svg {
      margin-left: 5px;
    }
  }

  .frisbuy-account-controls {
    padding: 18px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    background-color: #fff;
    z-index: 11;
  }

  .frisbuy-account-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    flex-shrink: 0;
    background-position: center;
    background-size: cover;
    margin-right: 10px;
  }

  .frisbuy-account-name {
    flex-grow: 1;
    color: #222222;
    font-size: 13px;
    text-decoration: none;
  }

  .frisbuy-account-subscribe {
    flex-shrink: 0;
    border: 1px solid #222222;
    padding: 6px 21px;
    font-size: 13px;
    color: #222222;
    text-decoration: none;

    &:hover {
      background-color: #222222;
      color: #fff;
    }
  }

  .frisbuy-hide-goods {
    position: absolute;
    height: 32px;
    width: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #222;
    border-radius: 100%;
    left: 16px;
    bottom: 16px;
    z-index: 11;
    cursor: pointer;

    @media screen and (max-width: 768px) {
      bottom: 7.5vh;
    }
  }

  @include screen-xs {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 999999999999;
    right: 0;
    bottom: 0;
    transition: none;
    background-color: #fff;
    transform: none !important;
    transition: none !important;

    &.frisbuy-slide-leave-active {
      transform: none;
    }

    &.frisbuy-slide-enter {
      transform: none;
    }

    .frisbuy-preview-markers {
      padding-bottom: 60px;
    }

    .frisbuy-account-controls {
      position: absolute;
      top: 2px;
      width: 100%;
      color: #fff;
      background-color: transparent;
      padding-right: 60px;
    }

    .frisbuy-preview-area {
      width: 100vw !important;
      flex-wrap: wrap;

      margin-top: 0 !important;

      video {
        width: 100%;
        height: 100%;
      }
    }

    .frisbuy-account-name {
      color: #fff;
    }

    .frisbuy-account-subscribe {
      border: 1px solid #fff;
      color: #fff;
    }

    .frisbuy-sr-branding {
      top: auto;
      bottom: 3px;
      left: unset;
      display: block !important;
      z-index: 10;

      svg {
        margin-left: 4px !important;
      }

      span {
        white-space: nowrap;
        justify-content: center;
      }
    }
  }

  @media (max-height: 700px) and (min-width: 768px) {
    &:before {
      width: 70px;
    }

    .frisbuy-account-name {
      max-width: 40%;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-right: 10px;
    }
  }
}

.frisbuy-sr-right {
  .frisbuy-preview-story {
    transform: translateX(calc(-100% - 80px));
    border-radius: 4px 0 0 4px;

    &.frisbuy-slide-enter {
      transform: translateX(0);
    }

    &.frisbuy-slide-leave-active {
      transform: translateX(0);
    }

    &:before {
      content: "";
      width: 80px;
      height: 100%;
      box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.24),
        0px 4px 16px rgba(0, 0, 0, 0.08);
      position: absolute;
      transform: translateX(80px);
      right: 0;
    }

    .frisbuy-preview-area {
      border-top-right-radius: 0;
      border-top-left-radius: 4px;
    }
  }
}

@media screen and (max-height: 425px) and (orientation: landscape) {
  .frisbuy-preview-story {
    /* TODO:
      see ".frisbuy-preview-story.frisbuy-preview-popup" same styles and remove double but in @media
    */
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    // background: rgba(#000000, 0.9) !important;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 99999999999999;

    &:before {
      content: unset;
    }

    .frisbuy-preview-good {
      min-width: 150px;
      max-width: 150px;
      font-size: 8px;
      .frisbuy-preview-good-prices {
        margin: 0;
      }
    }

    .frisbuy-story-slide {
      video {
        width: unset !important;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
      }
    }

    .frisbuy-preview-wrapper {
      height: 268px;
      flex-grow: unset !important;
    }

    .frisbuy-preview-area {
      order: 1;
      border-radius: 4px;
      -webkit-overflow-scrolling: auto;
    }

    .frisbuy-account-controls {
      order: 0;
      background-color: transparent;
      padding: 0;
      display: flex;
      flex-direction: column;
      .frisbuy-account-avatar {
        margin: 0;
      }
    }

    .frisbuy-account-name {
      color: #fff;
      max-width: unset;
      margin: 10px 0;
    }

    .frisbuy-account-subscribe {
      color: #fff;
      border-color: #fff;
    }

    .frisbuy-story-progress-bar {
      padding: 8px 0;
      margin-top: -15px;
    }

    .frisbuy-preview-markers {
      &.frisbuy-row-view {
        margin-top: -7px;
      }
    }

    .frisbuy-hide-goods {
      background-color: #fff;
      bottom: 9px;
      height: 30px;
      width: 30px;
      svg {
        path,
        line {
          fill: #222;
          stroke: #222;
        }
      }
    }

    .frisbuy-hide-goods {
      left: -40px;
    }
  }
}
</style>
